package com.evoapps.evoshift.ui.finance

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.domain.explain.Calculation
import com.evoapps.evoshift.domain.explain.DayExplanations
import com.evoapps.evoshift.domain.explain.Side
import com.evoapps.evoshift.domain.explain.SpmhExplanations
import com.evoapps.evoshift.domain.model.DailyRecord

/**
 * SH-152: [DailyRecord] worked out now from the month's shifts and cost snapshots (keeping what was
 * entered), for the people behind a saved day's labour cost; the daily results screen provides it.
 */
val LocalFreshDay = staticCompositionLocalOf<(DailyRecord) -> DailyRecord?> { { null } }

/**
 * SH-195: [DailyRecord] as its daily result counts it (DayResults.resultDay: the month's recurring
 * costs and revenue items a share of each day); the daily results screen provides it from the
 * month's ledger. The stored record (its other costs) stays as it is.
 */
val LocalResultDay = staticCompositionLocalOf<(DailyRecord) -> DailyRecord> { { it } }

/** SH-152: how a day's figures were calculated, each worked out only when its sheet opens. */
class DayWhy(
    private val record: DailyRecord,
    fresh: () -> DailyRecord?,
    /** SH-195: a day as its daily result counts it ([LocalResultDay]). */
    private val resultDay: (DailyRecord) -> DailyRecord = { it }
) {
    private val freshDay by lazy(fresh)
    private val hasTakings get() = record.revenue.realNetto != null

    val salesPlan: () -> Calculation? = { DayExplanations.salesTarget(record) }
    val takings: (() -> Calculation?)? get() = if (hasTakings) ({ DayExplanations.takings(record) }) else null

    /** Null for the actual side until the takings are in (the screen shows "-"). */
    private fun side(side: Side, make: () -> Calculation?): (() -> Calculation?)? = if (side == Side.ACTUAL && !hasTakings) null else make

    fun income(side: Side) = side(side) { DayExplanations.income(record, side, freshDay) }
    fun costs(side: Side) = side(side) { DayExplanations.costs(record, side, freshDay) }
    fun labourPercent(side: Side) = side(side) { DayExplanations.labourPercent(record, side, freshDay) }
    fun labourPercentOfSales(side: Side) = side(side) { DayExplanations.labourPercentOfSales(record, side, freshDay) }
    /** SH-195: the daily result, the recurring costs a share of the day ([LocalResultDay]). */
    fun result(side: Side) = side(side) { DayExplanations.result(resultDay(record), side, freshDay?.let(resultDay)) }
    /** SH-191: the day's SPMH: the target or the takings ÷ the hours. */
    fun spmh(side: Side) = side(side) { SpmhExplanations.day(record, side) }
}

/** [record]'s [DayWhy], kept while the record is the same. */
@Composable
fun rememberDayWhy(record: DailyRecord): DayWhy {
    val fresh = LocalFreshDay.current
    val resultDay = LocalResultDay.current
    return remember(record, fresh, resultDay) { DayWhy(record, { fresh(record) }, resultDay) }
}
